import type React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuth from '@/domains/auth/hooks/useAuth';
import { getFirstAccessibleRoute } from '@/shared/utils/navigation';
import { useAuthStore } from '@/store/authStore';

const Unauthorized: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user_id = useAuthStore((state) => state.user_id);
  const { handleAuthority } = useAuth({ userID: user_id });

  // Move the user on to the first page their authority does cover, so this
  // alert is a notice rather than a dead end. With no grants at all there is
  // nowhere to send them and the alert is the whole answer.
  const fallbackRoute = getFirstAccessibleRoute(handleAuthority);

  useEffect(() => {
    if (fallbackRoute) navigate(fallbackRoute, { replace: true });
  }, [fallbackRoute, navigate]);

  return (
    <div
      id="alert-additional-content-2"
      className="p-4 mb-4 text-red-800 border border-red-300 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-800"
      role="alert"
    >
      <div className="flex items-center">
        <svg
          className="shrink-0 w-4 h-4 me-2"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
        </svg>
        <span className="sr-only">{t('cmn > info')}</span>
        <h3 className="text-lg font-medium">{t('msg > unauthorized')}</h3>
      </div>
      <div className="mt-2 mb-4 text-sm">{t('msg > contact administrator')}</div>
    </div>
  );
};

export default Unauthorized;
